import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * Regras:
 * - precisa estar logado
 * - email confirmado
 * - profile completo (display_name e phone)
 *
 * Blindagem:
 * - timeout para nunca travar infinito se a query do profile "pendurar"
 * - se der erro ao ler profile, não trava: manda para complete-profile
 */
export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, loading, emailConfirmed } = useAuth();
  const location = useLocation();

  const [profileChecked, setProfileChecked] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);

  useEffect(() => {
    // enquanto auth ainda está carregando, não faz nada
    if (loading) return;

    // se não tem user, marca como checado para liberar redirect
    if (!user) {
      setHasProfile(false);
      setProfileChecked(true);
      return;
    }

    let cancelled = false;

    // timeout anti-trava
    const t = window.setTimeout(() => {
      if (cancelled) return;
      console.warn('[ProtectedRoute] profile check timeout -> fallback complete-profile');
      setHasProfile(false);
      setProfileChecked(true);
    }, 5000);

    const run = async () => {
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, display_name, phone')
          .eq('id', user.id)
          .maybeSingle();

        if (cancelled) return;

        if (error) {
          console.error('[ProtectedRoute] Error fetching profile:', error);
          setHasProfile(false);
        } else {
          const complete = !!data && !!data.display_name && !!data.phone;
          setHasProfile(complete);
        }
      } catch (e) {
        console.error('[ProtectedRoute] Unexpected error while checking profile:', e);
        if (!cancelled) setHasProfile(false);
      } finally {
        if (!cancelled) setProfileChecked(true);
        window.clearTimeout(t);
      }
    };

    run();

    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [user, loading]);

  // 1) Carregando auth
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // 2) Sem user => login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // 3) Email não confirmado => login com flag
  if (!emailConfirmed) {
    return <Navigate to="/login" state={{ needsEmailConfirmation: true }} replace />;
  }

  // 4) Ainda checando profile (máximo 5s por causa do timeout acima)
  if (!profileChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Carregando...</p>
        </div>
      </div>
    );
  }

  // 5) Profile incompleto => complete-profile
  if (!hasProfile && location.pathname !== '/complete-profile') {
    return <Navigate to="/complete-profile" replace />;
  }

  return <>{children}</>;
}